import { MFADescription, MFAInputWrapper, ResendLink } from './styles'
import { TFunction, Trans, useTranslation } from 'react-i18next'
import AuthTemplate from 'pages/Authentication/components/AuthTemplate'
import { BackLink } from 'pages/Authentication/components/styles'
import GraphQLHandler from 'components/GraphQLHandler'
import AuthTitle from 'pages/Authentication/components/AuthTitle'
import * as Yup from 'yup'
import { Formik } from 'formik'
import ValidateOnEnter from 'components/ValidateOnEnter'
import { useMutation } from '@apollo/client'
import {
  MFAResendResult,
  MFAVerifyResult,
  MFAVerifyVariables,
  MFA_VERIFY,
  MFA_RESEND
} from 'pages/Authentication/MFA/mutations'
import { displayErrors, DoMutation, hasNoError } from 'globals/graphql'
import MFAImage from './assets/mfa.png'
import Input from 'components/Input'
import SubmitButton from 'components/SubmitButton'
import Security from 'pages/components/Security'
import { useHistory } from 'react-router-dom'

interface MFAForm {
  token?: string
}

const initialValues: MFAForm = {}

const validation = (t: TFunction): Yup.SchemaOf<MFAForm> =>
  Yup.object({
    token: Yup.string().required(t('global.error.required', { field: t('global.code') }))
  })

const MFA = () => {
  const { t } = useTranslation()
  const mfaVerify = useMutation<MFAVerifyResult, MFAVerifyVariables>(MFA_VERIFY, displayErrors)
  const mfaResend = useMutation<MFAResendResult>(MFA_RESEND, displayErrors)
  const history = useHistory()
  const logout = () => {
    localStorage.removeItem('token')
    history.push('/login')
  }

  return (
    <AuthTemplate
      headerRightContent={
        <BackLink to='/login' onClick={() => logout()}>
          {t('mfa.back')}
        </BackLink>
      }
      rightPartImg={MFAImage}
      rightPartMaxWidth={510}
    >
      <Security noCenter>
        {(currentUser) => (
          <>
            <AuthTitle
              title={
                <Trans i18nKey='mfa.title'>
                  <b>Authentification</b> à deux facteurs
                </Trans>
              }
            />

            <GraphQLHandler
              mutations={{
                mfaVerify,
                mfaResend
              }}
            >
              {({
                mutations: {
                  mfaVerify: [doMFAVerify],
                  mfaResend: [doMFAResend]
                }
              }) => (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validation(t)}
                  onSubmit={onSubmitMFAForm(doMFAVerify)}
                  isInitialValid={false}
                >
                  {({ handleSubmit }) => (
                    <ValidateOnEnter handleSubmit={handleSubmit} name='mfa'>
                      <MFADescription>{t('mfa.description', { phone: currentUser.phoneLast4Digits })}</MFADescription>

                      <MFAInputWrapper>
                        <Input
                          type='text'
                          name='token'
                          placeholder='&#11044;&#11044;&#11044;&#11044;&#11044;&#11044;'
                          maxLength={6}
                          spellcheck={false}
                          hideError
                        />

                        <ResendLink onClick={onResendCode(doMFAResend)}>{t('global.resend')}</ResendLink>
                      </MFAInputWrapper>

                      <SubmitButton text={t('global.continue')} />
                    </ValidateOnEnter>
                  )}
                </Formik>
              )}
            </GraphQLHandler>
          </>
        )}
      </Security>
    </AuthTemplate>
  )
}

const onSubmitMFAForm = (doMFAVerify: DoMutation<MFAVerifyResult, MFAVerifyVariables>) => (values: MFAForm) => {
  if (values.token) {
    doMFAVerify({
      variables: {
        token: values.token
      }
    }).then(
      hasNoError((result: MFAVerifyResult) => {
        localStorage.setItem('token', result.userMfaVerify.token)
        window.location.href = '/clients'
      })
    )
  }
}

const onResendCode = (doResendCode: DoMutation<MFAResendResult>) => () => doResendCode()

export default MFA
